import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  Divider,
} from '@mui/material';
import { CalendarToday, Google, Login as LoginIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useAuth } from '@/hooks/useAuth';
import { useAppDispatch } from '@/hooks/redux';
import { clearError } from '@/store/authSlice';
import { oauthService, OAuthConfig } from '@/services/oauthService';
import api from '@/services/api';
import { IS_ADMIN_PORTAL } from '@/utils/adminDetection';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
});

type FormData = yup.InferType<typeof schema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { login, loading, error } = useAuth();
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null);
  const [oauthLoading, setOauthLoading] = useState(!IS_ADMIN_PORTAL);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [signupsEnabled, setSignupsEnabled] = useState<boolean | null>(null);

  // Check signup status
  useEffect(() => {
    const checkSignupStatus = async () => {
      try {
        const response = await api.get('/auth/signup-status');
        setSignupsEnabled(response.data.signupsEnabled);
      } catch {
        setSignupsEnabled(true);
      }
    };
    checkSignupStatus();
  }, []);

  // Check for OAuth error from redirect
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const provider = searchParams.get('provider');

    if (errorParam === 'not_linked' && provider) {
      setOauthError(`Login failed. No account found for that ${provider} email.`);
    } else if (errorParam === 'no_email' && provider) {
      setOauthError(`Login failed. Your ${provider} account did not provide an email address.`);
    } else if (errorParam === 'signups_disabled') {
      setOauthError('Public registration is currently disabled. Please use an invitation link to join.');
    } else if (errorParam === 'access_denied') {
      setOauthError('Access denied.');
    }
  }, [searchParams]);

  // Load OAuth configuration
  useEffect(() => {
    const loadOAuthConfig = async () => {
      try {
        const config = await oauthService.getConfig();
        setOauthConfig(config);
      } catch {
        setOauthConfig(null);
      } finally {
        setOauthLoading(false);
      }
    };
    loadOAuthConfig();
  }, []);

  // Clear any stale errors (e.g., "Session expired" from session restore) on mount
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData, event?: React.BaseSyntheticEvent) => {
    event?.preventDefault();
    const result = await login(data.email, data.password);
    if (result.success) {
      const returnTo = searchParams.get('returnTo');
      navigate(returnTo || '/');
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {IS_ADMIN_PORTAL ? (
            <Box component="img" src="/icons/admin-favicon.svg" alt="Admin" sx={{ m: 1, width: 64, height: 64 }} />
          ) : (
            <Avatar sx={{
              m: 1,
              width: 56,
              height: 56,
              bgcolor: '#10b981'
            }}>
              <CalendarToday fontSize="large" />
            </Avatar>
          )}
          <Typography component="h1" variant="h4" gutterBottom>
            {IS_ADMIN_PORTAL ? 'PLCN Admin Console' : 'Player Central'}
          </Typography>
        </Link>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {IS_ADMIN_PORTAL ? 'Sign in to continue' : 'Sign in to your account'}
        </Typography>

        <Card sx={{ mt: 3, width: '100%', minHeight: oauthLoading ? 300 : undefined }}>
          <CardContent sx={{ p: 4 }}>
            {oauthLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (<>
            {(error || oauthError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {oauthError || error}
              </Alert>
            )}

            {/* OAuth Buttons (hidden on admin portal) */}
            {!IS_ADMIN_PORTAL && oauthConfig?.google.enabled && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                  {oauthConfig.google.enabled && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Google />}
                      onClick={() => oauthService.loginWithGoogle({ returnTo: searchParams.get('returnTo') || undefined })}
                      sx={{
                        borderColor: '#4285F4',
                        color: '#4285F4',
                        '&:hover': {
                          borderColor: '#357ABD',
                          backgroundColor: 'rgba(66, 133, 244, 0.04)',
                        },
                      }}
                    >
                      Continue with Google
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    or sign in with email
                  </Typography>
                </Divider>
              </>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                {...register('email')}
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              <TextField
                {...register('password')}
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
                startIcon={loading ? undefined : <LoginIcon />}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
              <Box textAlign="center" sx={{ mb: 1 }}>
                <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary">
                    Forgot password?
                  </Typography>
                </Link>
              </Box>
              {!IS_ADMIN_PORTAL && signupsEnabled && (
                <Box textAlign="center">
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <Typography variant="body2" color="primary">
                      Don't have an account? Sign Up
                    </Typography>
                  </Link>
                </Box>
              )}
            </Box>
            </>)}
          </CardContent>
        </Card>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {IS_ADMIN_PORTAL
              ? 'Player Central administration console'
              : 'Track your players\' schedules across multiple leagues with intelligent analysis'}
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
